import { useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X, AlertTriangle } from "lucide-react";
import { adminApi } from "@/lib/sleek";

export const SyncToProd = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const push = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await adminApi(token).post("/admin/sync-to-production", { password });
      toast.success(
        `Pushed to production: landing page, ${data.synced.portfolio} portfolio projects, ${data.synced.showroom_photos} showroom photos.`,
        { duration: 8000 }
      );
      setOpen(false);
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed — try again.", { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-sm flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 hover:bg-zinc-800 transition"
        data-testid="sync-to-prod-btn"
      >
        <UploadCloud size={14} /> Push to Production
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-6" data-testid="sync-to-prod-dialog">
          <form onSubmit={push} className="bg-white w-full max-w-lg p-8 relative">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-950" data-testid="sync-dialog-close" aria-label="Close">
              <X size={18} />
            </button>
            <div className="font-display text-2xl">Push content to production</div>
            <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
              Copies the landing page config, all portfolio projects, and the showroom gallery from
              this preview to <b>sleekrenovations.com</b>. The live site updates instantly.
            </p>
            <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 leading-relaxed">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                Photo <b>files</b> ship with the code, not the database. If you added brand-new photos
                since the last deploy, hit <b>Redeploy</b> first — then push content.
              </span>
            </div>
            <input
              type="password"
              placeholder="Admin password (to authorize on production)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-3 mt-5 text-sm focus:outline-none focus:border-zinc-950"
              data-testid="sync-password-input"
              required
            />
            <div className="flex gap-3 mt-6">
              <button
                type="submit"
                disabled={busy || !password}
                className="flex-1 bg-zinc-950 text-white py-3 text-sm hover:bg-zinc-800 transition disabled:opacity-50"
                data-testid="sync-confirm-btn"
              >
                {busy ? "Pushing…" : "Push to production"}
              </button>
              <button type="button" onClick={() => setOpen(false)} className="px-6 border border-zinc-300 text-sm hover:border-zinc-950 transition" data-testid="sync-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default SyncToProd;
